import React from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { format, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MessageSquare, Calendar } from 'lucide-react';

interface UsageStatsProps {
  conversationCount: number;
  maxConversations: number;
  renewalDate: Date;
}

export function UsageStats({ conversationCount, maxConversations, renewalDate }: UsageStatsProps) {
  const { subscription } = useSubscription();
  const percentage = (conversationCount / maxConversations) * 100;

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <h3 className="text-lg font-semibold text-white mb-6">Current month's usage</h3>
      
      <div className="space-y-6">
        {/* Conversations utilisées */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-gray-300">
              <MessageSquare className="w-5 h-5" />
              <span>Conversations</span>
            </div>
            <span className="text-gray-300">
              {conversationCount} / {maxConversations === -1 ? '∞' : maxConversations}
            </span>
          </div>
          
          {maxConversations !== -1 && (
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
          )}
        </div>

        {/* Date de renouvellement */}
        <div className="flex items-center gap-3 text-gray-300">
          <Calendar className="w-5 h-5" />
          <span>
            Renewal on {format(renewalDate, 'dd MMMM yyyy', { locale: fr })}
          </span>
        </div>

        {/* Upgrade CTA si proche de la limite */}
        {percentage > 80 && subscription?.tier !== 'enterprise' && (
          <div className="bg-indigo-500/20 text-indigo-400 p-4 rounded-lg">
            <p className="mb-2">
              You are approaching your monthly conversation limit.
            </p>
            <a 
              href="/pricing"
              className="text-sm hover:text-indigo-300 transition-colors"
            >
              Upgrade to a higher plan →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}